// components/portal/SourceRentRollCell.tsx
'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check } from 'lucide-react';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { canEditSourceRentRoll } from '#lib/access/sourceAccess';
import { rentRollValue } from '#lib/leasing/sourceRentRoll';
import { UnitType, type SourceRentRollField } from '#types/types';

const labelFor = (value: string) => value.toLowerCase().replace(/_/g, ' ').replace(/^./, c => c.toUpperCase());
// Status and unit type are words, not coloured pills (ChatGPT style, owner 2026-09-29).
export function RentRollTag({ value }: { value: string }) {
  return <span className="truncate">{value === 'true' ? 'Yes' : value === 'false' ? 'No' : labelFor(value)}</span>;
}

export function RentRollMenu({ anchor, label, onClose, children }: {
  anchor: HTMLElement; label: string; onClose: (restoreFocus?: boolean) => void; children: React.ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [position] = useState(() => {
    const r = anchor.getBoundingClientRect();
    const width = Math.min(340, window.innerWidth - 16);
    const maxHeight = Math.min(340, window.innerHeight - 32);
    return { width, maxHeight, left: Math.max(8, Math.min(r.left, window.innerWidth - width - 8)), top: Math.max(8, Math.min(r.bottom + 4, window.innerHeight - maxHeight - 8)) };
  });
  useEffect(() => {
    (ref.current?.querySelector<HTMLElement>('input, textarea, button') ?? ref.current)?.focus();
    const outside = (event: MouseEvent) => {
      if ((event.target as HTMLElement | null)?.closest?.('[data-rentroll-menu]')) return;
      if (!ref.current?.contains(event.target as Node) && !anchor.contains(event.target as Node)) onClose(false);
    };
    const scroll = (event: Event) => { if (!(event.target as HTMLElement | null)?.closest?.('[data-rentroll-menu]')) onClose(false); };
    const resize = () => onClose(false);
    document.addEventListener('mousedown', outside);
    window.addEventListener('scroll', scroll, true);
    window.addEventListener('resize', resize);
    return () => {
      document.removeEventListener('mousedown', outside);
      window.removeEventListener('scroll', scroll, true);
      window.removeEventListener('resize', resize);
    };
  }, [anchor]);
  return createPortal(<div ref={ref} tabIndex={-1} data-rentroll-menu role="dialog" aria-label={label} style={position}
    className="fixed z-[1200] overflow-y-auto rounded-[12px] border border-white/[0.06] bg-[#353535] p-1 text-[13px] text-[#D4D4D4] shadow-xl"
    onClick={event => event.stopPropagation()} onKeyDown={event => {
      event.stopPropagation();
      if (event.key === 'Escape') { event.preventDefault(); onClose(true); }
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        if ((event.target as HTMLElement).tagName === 'TEXTAREA') return;
        event.preventDefault();
        const buttons = [...(ref.current?.querySelectorAll<HTMLButtonElement>('button:not(:disabled)') ?? [])];
        const index = buttons.indexOf(document.activeElement as HTMLButtonElement);
        const next = index < 0 ? event.key === 'ArrowDown' ? 0 : buttons.length - 1 : (index + (event.key === 'ArrowDown' ? 1 : -1) + buttons.length) % buttons.length;
        buttons[next]?.focus();
      }
    }}>{children}</div>, document.body);
}

function inputValue(value: unknown, field: SourceRentRollField) {
  return field.kind === 'boolean' ? value == null ? '' : String(value) : rentRollValue(value, field.kind);
}

export const RentRollCell = React.memo(function RentRollCell({ propertyId, entity, id, field }: {
  propertyId: string; entity: 'Unit' | 'Deal'; id?: string; field: SourceRentRollField;
}) {
  const { kernel } = useKernel();
  const record = useKernelStore(s => id ? s.inventory[entity].registry[id] : undefined);
  const user = useKernelStore(s => s.userContext);
  const owners = useKernelStore(s => s.inventory.Property.registry[propertyId]?.leasingManagerIds);
  const runtime = useKernelStore(s => id ? s.entityKernelState[`${entity}:${id}`] : undefined);
  const edit = useMemo(() => id ? kernel.current?.readEntityRuntimeState(entity, id).sourceRentRollEdit : undefined, [runtime, entity, id]);
  const [editing, setEditing] = useState(false);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [query, setQuery] = useState('');
  const button = useRef<HTMLButtonElement>(null);
  const composing = useRef(false);
  const openingDraft = useRef<{ value: string; error?: string } | null>(null);
  const focusAfterCommit = useRef(false);
  const suppressBlur = useRef(false);
  const data = record as unknown as Record<string, unknown> | undefined;
  const saved = inputValue(data?.[field.key], field);
  const pending = Boolean(edit && Object.hasOwn(edit.values, field.key));
  const value = pending ? edit!.values[field.key] ?? '' : saved;
  const readonly = !record?.updatedAt || !canEditSourceRentRoll(user, owners) || field.kind === 'readonly';
  const blocked = Boolean(edit?.saving || edit?.refreshRequired || (edit && !pending && Object.keys(edit.values).length));
  useEffect(() => {
    if (!focusAfterCommit.current || editing || anchor) return;
    if (document.activeElement !== document.body && document.activeElement !== button.current) {
      focusAfterCommit.current = false;
      return;
    }
    if (!blocked) { button.current?.focus(); focusAfterCommit.current = false; }
  }, [editing, anchor, blocked]);
  const categorical = field.kind === 'unitType' || field.kind === 'boolean';
  const tag = categorical || field.key === 'status';
  const longText = ['description', 'contactNotes', 'leaseBreakOptions'].includes(field.key);
  const explanation = !id ? 'No signed current lease. Select a recorded lease to inspect its terms.' : !canEditSourceRentRoll(user, owners)
    ? 'Read only. Only Fater admins and assigned asset owners can edit.' : field.key === 'status'
      ? 'Calculated or legal status. Change the underlying lease through its workflow, not the rent-roll cell.' : 'Calculated value. This cell is read only.';
  const read = () => id ? kernel.current?.readEntityRuntimeState(entity, id).sourceRentRollEdit : undefined;
  const patchValue = (next: string) => {
    const k = kernel.current;
    if (!k || !id || field.kind === 'readonly') return false;
    const current = k.getInventory()[entity].registry[id];
    const draft = read();
    if (!current?.updatedAt || draft?.saving || draft?.refreshRequired || !canEditSourceRentRoll(k.getUserContext(), k.getInventory().Property.registry[propertyId]?.leasingManagerIds)) return false;
    if (draft && Object.keys(draft.values).length && !Object.hasOwn(draft.values, field.key)) return false;
    k.patchEntityRuntimeState(entity, id, { sourceRentRollEdit: { ...draft, error: next === draft?.values[field.key] ? draft?.error : undefined, values: { ...draft?.values, [field.key]: next }, expectedUpdatedAt: draft?.expectedUpdatedAt ?? current.updatedAt } });
    return true;
  };
  const discard = () => {
    const k = kernel.current, draft = read();
    if (!k || !id || !draft || draft.saving) return;
    const values = { ...draft.values }; delete values[field.key];
    k.patchEntityRuntimeState(entity, id, { sourceRentRollEdit: Object.keys(values).length ? { ...draft, values } : undefined });
  };
  const cancelEdit = () => {
    const k = kernel.current, draft = read(), original = openingDraft.current;
    if (k && id && draft && !draft.saving && original) {
      k.patchEntityRuntimeState(entity, id, { sourceRentRollEdit: { ...draft, error: original.error, values: { ...draft.values, [field.key]: original.value } } });
    } else if (!original) discard();
  };
  const commit = () => {
    const k = kernel.current, draft = read();
    if (!k || !id || !draft || draft.saving || !Object.hasOwn(draft.values, field.key)) return;
    const canonical = k.getInventory()[entity].registry[id] as unknown as Record<string, unknown> | undefined;
    if (!draft.error && draft.values[field.key] === inputValue(canonical?.[field.key], field)) { discard(); return; }
    return k.saveSourceRentRollRecord(propertyId, entity, id, field.key);
  };
  const close = (restore = false) => {
    if (!readonly && (categorical || restore)) { suppressBlur.current = true; cancelEdit(); }
    else if (!readonly && longText && !read()?.error) commit();
    focusAfterCommit.current = restore;
    setAnchor(null);
  };
  const activate = (target: HTMLButtonElement) => {
    if (blocked) return;
    suppressBlur.current = false;
    if (readonly) { setAnchor(target); return; }
    const previous = read();
    openingDraft.current = previous && Object.hasOwn(previous.values, field.key) ? { value: previous.values[field.key] ?? '', error: previous.error } : null;
    if (!patchValue(value)) return;
    if (categorical || longText || edit?.error) { setQuery(''); setAnchor(target); }
    else setEditing(true);
  };
  const onKey = (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    event.stopPropagation();
    if (event.key === 'Escape') {
      event.preventDefault(); suppressBlur.current = true; cancelEdit(); focusAfterCommit.current = true;
      event.currentTarget.blur(); setEditing(false); setAnchor(null);
    } else if (event.key === 'Enter' && !event.nativeEvent.isComposing && !composing.current && (!longText || event.ctrlKey || event.metaKey)) {
      event.preventDefault(); focusAfterCommit.current = true;
      if (read()?.error) { suppressBlur.current = true; void commit(); }
      event.currentTarget.blur(); setAnchor(null);
    }
  };
  const editorProps = {
    'aria-label': field.label, value, autoFocus: true,
    onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => patchValue(event.target.value),
    onBlur: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (composing.current) return;
      const discarding = (event.relatedTarget as HTMLElement | null)?.dataset.rentrollDiscard === `${entity}:${id}:${field.key}`;
      if (!suppressBlur.current && !discarding && !read()?.error) void commit();
      setEditing(false);
    }, onKeyDown: onKey,
    onCompositionStart: () => { composing.current = true; }, onCompositionEnd: () => { composing.current = false; },
  };
  const options = field.kind === 'unitType' ? Object.keys(UnitType) : ['false', 'true'];
  return <div className="relative h-10 min-w-0" data-rentroll-field={field.key} data-record-id={id}>
    {editing ? <input {...editorProps} type={field.kind === 'date' ? 'date' : 'text'} inputMode={field.kind === 'number' ? 'decimal' : undefined}
      className="h-full w-full rounded-[8px] bg-[#1B1B1B] px-2.5 text-[13px] text-[#ECECEC] outline outline-1 -outline-offset-1 outline-[#444444]" /> :
      <button ref={button} type="button" aria-label={`${field.label}: ${value || 'Empty'}`} aria-haspopup={categorical || readonly ? 'dialog' : undefined} disabled={blocked}
        onClick={event => activate(event.currentTarget)} title={readonly ? explanation : value || 'Click to edit'}
        className={`flex h-full w-full min-w-0 items-center gap-1 px-2.5 text-left text-[13px] outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#2383E2] disabled:cursor-wait ${field.kind === 'number' ? 'justify-end tabular-nums' : ''} ${pending ? 'bg-white/[0.06]' : ''}`}>
        {tag && value !== '' ? <RentRollTag value={value} /> : <span className="truncate">{value}</span>}
        {pending && <span className={edit?.error ? 'text-[#F28B82]' : 'text-[#8E8E8E]'} title={edit?.error ?? 'Unsaved'}>{edit?.saving ? '…' : edit?.error ? '!' : '·'}</span>}
      </button>}
    {anchor && <RentRollMenu anchor={anchor} label={field.label} onClose={close}>
      {readonly ? <div className="space-y-2 p-3 leading-relaxed"><p className="whitespace-pre-wrap break-words">{value || 'Empty'}</p><p className="text-xs text-white/50">{explanation}</p></div> : <>
        {edit?.error && <div role="alert" className="p-2 text-xs text-[#F28B82]">
          <p>{edit.error} Your unsaved input is retained.</p>
          <div className="mt-2 flex gap-3"><button onClick={commit}>Retry</button><button data-rentroll-discard={`${entity}:${id}:${field.key}`} onMouseDown={event => event.preventDefault()} onClick={() => {
            discard(); setAnchor(null);
            if (id) void kernel.current?.syncEntity(entity, id, { force: true, requireFresh: true }).catch(error => kernel.current?.handleError(String(error)));
          }}>Discard cell and reload</button></div>
        </div>}
        {categorical ? <>
          <input aria-label={`Search ${field.label}`} autoFocus value={query} onChange={event => setQuery(event.target.value)} placeholder="Search options…" className="mb-1 w-full border-b border-white/10 bg-transparent p-2 outline-none" />
          <div role="listbox" aria-label={`${field.label} options`}>
            {options.filter(option => labelFor(option).toLowerCase().includes(query.toLowerCase()) || (option === 'true' ? 'yes' : option === 'false' ? 'no' : '').includes(query.toLowerCase())).map(option =>
              <button key={option} role="option" aria-selected={value === option} disabled={edit?.saving} className="flex w-full items-center justify-between rounded px-2 py-1.5 hover:bg-white/5 focus:bg-white/10 focus:outline-none" onClick={() => {
                if (patchValue(option)) void commit(); focusAfterCommit.current = true; setAnchor(null);
              }}><RentRollTag value={option} />{value === option && <Check size={13} />}</button>)}
          </div>
        </> : <textarea {...editorProps} rows={longText ? 5 : 2} className="w-full resize-y bg-transparent p-2 text-[13px] outline-none" />}
      </>}
    </RentRollMenu>}
  </div>;
});
